// ============================================================================
// BOQ — Source files: which bidder's rates read as Estimate, which as Tender
// ============================================================================
// Backs Settings → Source files. Lists every boq.rate_source_documents row with
// the library versions it created and its rates broken down per bidder
// (boq.source_file_bidders), and moves a bidder's rates between the Estimate
// (rate_quoted) and Tender (rate_billed) columns through boq.set_rate_basis.
//
// Only curators may move rates (boq.can_curate_library: admins + the named
// allow-list); `canClassify` mirrors that so the page disables what the RPC
// would refuse. The RPC re-checks, so the flag is a courtesy, not the guard.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { SOURCE_FILES_BUCKET, sourceFilePath } from '../utils/sourceFileStorage';

const num = (v) => Number(v || 0);

export const useBoqSourceFiles = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canClassify, setCanClassify] = useState(false);

  // quiet: re-read without raising `loading`, so a save does not blank the page.
  const fetchAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [docRes, bidRes, itemRes, gateRes, sdRes] = await Promise.all([
        supabase.schema('boq').from('rate_source_documents')
          .select('id, filename, document_date, consultant, note, boq_stage, storage_path')
          .order('document_date', { ascending: false }),
        supabase.schema('boq').from('source_file_bidders')
          .select('document_id, contractor, rate_count, estimate_count, tender_count, both_count'),
        supabase.schema('boq').from('source_file_item_counts').select('document_id, version_count'),
        supabase.schema('boq').rpc('can_curate_library'),
        // SD area rates per source file. A small table, so counted here.
        supabase.schema('boq').from('sd_rates').select('source_document_id').not('source_document_id', 'is', null),
      ]);
      if (docRes.error) throw docRes.error;
      if (bidRes.error) throw bidRes.error;
      if (itemRes.error) throw itemRes.error;
      if (sdRes.error) throw sdRes.error;
      const sdRatesByDoc = new Map();
      (sdRes.data || []).forEach((r) => sdRatesByDoc.set(r.source_document_id, (sdRatesByDoc.get(r.source_document_id) || 0) + 1));

      const versionsByDoc = new Map((itemRes.data || []).map((r) => [r.document_id, num(r.version_count)]));
      const biddersByDoc = new Map();
      (bidRes.data || []).forEach((b) => {
        if (!biddersByDoc.has(b.document_id)) biddersByDoc.set(b.document_id, []);
        biddersByDoc.get(b.document_id).push({
          contractor: b.contractor,
          rateCount: num(b.rate_count),
          estimateCount: num(b.estimate_count),
          tenderCount: num(b.tender_count),
          bothCount: num(b.both_count),
        });
      });

      setFiles((docRes.data || []).map((d) => {
        const bidders = (biddersByDoc.get(d.id) || [])
          .sort((a, b) => (a.contractor || '').localeCompare(b.contractor || ''));
        const sum = (k) => bidders.reduce((t, b) => t + b[k], 0);
        return {
          id: d.id,
          filename: d.filename,
          documentDate: d.document_date,
          consultant: d.consultant,
          note: d.note,
          // The uploaded copy behind the Rates page's source preview, if any.
          storagePath: d.storage_path || null,
          // 'sd' for a budget / costing workbook behind an SD estimate.
          isSdSource: d.boq_stage === 'sd',
          sdRateCount: sdRatesByDoc.get(d.id) || 0,
          versionsCreated: versionsByDoc.get(d.id) || 0,
          bidders,
          rateCount: sum('rateCount'),
          estimateCount: sum('estimateCount'),
          tenderCount: sum('tenderCount'),
          bothCount: sum('bothCount'),
        };
      }));
      setCanClassify(!gateRes.error && gateRes.data === true);
    } catch (err) {
      console.error('[useBoqSourceFiles] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'source files' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Move one bidder's rates (or, with allBidders, the whole file's) to a basis.
  // Returns how many rates moved.
  const setBasis = useCallback(async ({ documentId, contractor = null, allBidders = false, basis }) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return { success: false, error: 'Please sign in.' };
      const { data, error: rpcErr } = await supabase.schema('boq').rpc('set_rate_basis', {
        p_document_id: documentId,
        p_contractor: contractor,
        p_all_bidders: allBidders,
        p_basis: basis,
      });
      if (rpcErr) throw rpcErr;
      await fetchAll({ quiet: true });
      return { success: true, moved: Number(data || 0) };
    } catch (err) {
      console.error('[useBoqSourceFiles] setBasis failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'rate basis' }) };
    }
  }, [fetchAll]);

  // Upload (or replace) the file a document row stands for, so its rates can
  // show the page or rows they came from. Curators only: the bucket's write
  // policy and the RPC both check boq.can_curate_library().
  const uploadFile = useCallback(async ({ documentId, filename, file }) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return { success: false, error: 'Please sign in.' };
      const path = sourceFilePath(documentId, filename || file.name);
      const { error: upErr } = await supabase.storage.from(SOURCE_FILES_BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type || undefined });
      if (upErr) throw upErr;
      const { error: rpcErr } = await supabase.schema('boq').rpc('set_source_file_storage_path', {
        p_document_id: documentId, p_path: path,
      });
      if (rpcErr) throw rpcErr;
      await fetchAll({ quiet: true });
      return { success: true };
    } catch (err) {
      console.error('[useBoqSourceFiles] upload failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'upload', entity: 'source file' }) };
    }
  }, [fetchAll]);

  return { files, loading, error, canClassify, refresh: () => fetchAll(), setBasis, uploadFile };
};

export default useBoqSourceFiles;
